import { css } from '@emotion/css';
import { Input, InputNumber, Typography, theme } from 'antd';
import type React from 'react';
import { LocalizationFieldType } from '../../../graphql/generated/graphql.ts';
import type { LocalizationFieldValue } from '../../../hooks/store/useAppStore.ts';

export interface LocalizationFieldConfig {
  label: string;
  inputType: LocalizationFieldType;
  unit?: string | null;
  mandatory: boolean;
}

/** Renders the Material's extra fields for one selected Lokalisation. */
export const LocalizationFieldInputs: React.FC<{
  fields: readonly LocalizationFieldConfig[];
  values: LocalizationFieldValue[];
  showErrors: boolean;
  onChange: (values: LocalizationFieldValue[]) => void;
}> = ({ fields, values, showErrors, onChange }) => {
  const { token } = theme.useToken();

  if (!fields.length) {
    return null;
  }

  const readValue = (label: string) => values.find(it => it.label === label)?.value ?? '';

  const set = (label: string, value: string) => {
    const rest = values.filter(it => it.label !== label);
    onChange(value === '' ? rest : [...rest, { label, value }]);
  };

  /** Fields flow into as many columns as fit; at modal width that is three, on a phone one. */
  const containerStyle = css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
    gap: ${token.marginXXS}px ${token.marginSM}px;
    align-items: start;
  `;

  const fieldStyle = css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    /* both control kinds fill the column, so the fields keep one edge */
    .ant-input-number,
    .ant-input-number-group-wrapper {
      width: 100%;
    }
  `;

  const labelStyle = css`
    font-size: ${token.fontSizeSM}px;
  `;

  /** Same red the field turns when it is left empty, so the marker and the error agree. */
  const mandatoryStyle = css`
    color: ${token.colorError};
  `;

  const messageStyle = css`
    font-size: ${token.fontSizeSM}px;
  `;

  return (
    <div className={containerStyle}>
      {fields.map(field => {
        const value = readValue(field.label);
        const missing = showErrors && field.mandatory && !value.trim();
        const notNumeric =
          showErrors && field.inputType === LocalizationFieldType.NUMBER && !!value.trim() && !Number.isFinite(Number(value));

        return (
          <div key={field.label} className={fieldStyle}>
            <Typography.Text type="secondary" className={labelStyle}>
              {field.label}
              {field.mandatory && <span className={mandatoryStyle}> *</span>}
            </Typography.Text>
            {field.inputType === LocalizationFieldType.NUMBER ? (
              <InputNumber
                size="small"
                inputMode="decimal"
                // stringMode keeps the value a string end to end, so a large or small number does
                // not round-trip through String(Number) and come back as "1e+21". decimalSeparator
                // matches the rest of the app; the value stays dot-separated regardless.
                stringMode
                decimalSeparator=","
                status={missing || notNumeric ? 'error' : undefined}
                addonAfter={field.unit || undefined}
                value={value === '' ? null : value}
                onChange={v => set(field.label, v === null || v === undefined ? '' : String(v))}
              />
            ) : (
              <Input size="small" status={missing ? 'error' : undefined} value={value} onChange={e => set(field.label, e.target.value)} />
            )}
            {missing && (
              <Typography.Text type="danger" className={messageStyle}>
                erforderlich
              </Typography.Text>
            )}
            {notNumeric && (
              <Typography.Text type="danger" className={messageStyle}>
                muss eine Zahl sein
              </Typography.Text>
            )}
          </div>
        );
      })}
    </div>
  );
};
